import React from "react";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import type { DeficiencyDocument, DeficiencyTaskDecision } from "@services/deficiencies/types";
import { useTranslation } from "react-i18next";
import { useDownloadDeficiencyAttachment } from "../hooks/useDownloadDeficiencyAttachment";
import { getApprovalTaskDecisionAttachments, getRejectionTaskDecisionAttachments } from "./utils";

type StatusDetailsDrawerProps = {
  isOpen: boolean;
  onClose: (drawerName: "detailsDrawer") => void;
  taskDecision?: DeficiencyTaskDecision | null;
};

const SERVICE = "bff-deficiency";

const StatusDetailsDrawer: React.FC<StatusDetailsDrawerProps> = ({
  isOpen,
  onClose,
  taskDecision,
}) => {
  const { t } = useTranslation();
  const { handleDownload, handlePreview } = useDownloadDeficiencyAttachment();

  const isApprovedDecision = taskDecision?.status === "APPROVED";
  const comments = isApprovedDecision ? taskDecision?.approvalNotes : taskDecision?.rejectionNotes;
  const attachments = isApprovedDecision
    ? getApprovalTaskDecisionAttachments(taskDecision)
    : getRejectionTaskDecisionAttachments(taskDecision);

  const title = isApprovedDecision
    ? t("deficiencies.status-details.approved-title")
    : t("deficiencies.status-details.rejected-title");

  const renderAttachment = (document: DeficiencyDocument, index: number) => {
    return (
      <div
        key={`${document.documentUrl}-${index}`}
        className="border rounded p-3 w-full border-[#F1F2F4]"
      >
        <div className="flex justify-between items-center w-full">
          <a
            href={document.documentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline break-all cursor-pointer"
            data-testid={`status-details-attachment-${index}-link`}
            onClick={(e) => {
              e.preventDefault();
              void handlePreview(SERVICE, document.documentUrl);
            }}
          >
            {document.documentName}
          </a>

          <button
            type="button"
            data-testid={`status-details-attachment-${index}-download`}
            onClick={() =>
              void handleDownload(SERVICE, document.documentUrl, document.documentName)
            }
            className="cursor-pointer"
            title={t("common.download")}
          >
            <FileDownloadOutlinedIcon />
          </button>
        </div>
      </div>
    );
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div data-testid="status-details-drawer" className="min-w-[400px]">
      <SideDrawer
        isOpen={isOpen}
        title={title}
        onClose={() => onClose("detailsDrawer")}
        submitLabel={t("common.close")}
        onSubmit={() => onClose("detailsDrawer")}
        hideCancelButton
      >
        <div className="w-[400px] p-6 space-y-4">
          {comments && (
            <div data-testid="status-details-comments">
              <div className="text-sm font-bold mb-1">
                {t("deficiencies.status-details.comments")}
              </div>
              <p className="text-sm whitespace-pre-wrap">{comments}</p>
            </div>
          )}

          {attachments.length > 0 && (
            <div data-testid="status-details-attachments">
              <div className="text-sm font-bold mb-1">
                {t("deficiencies.status-details.attachments")}
              </div>
              <div className="space-y-3">
                {attachments.map((document, index) => renderAttachment(document, index))}
              </div>
            </div>
          )}

          {!comments && attachments.length === 0 && (
            <div className="text-sm text-gray-500">
              {t("deficiencies.status-details.no-details")}
            </div>
          )}
        </div>
      </SideDrawer>
    </div>
  );
};

export default StatusDetailsDrawer;
